/**
 * Fatura kalemi kuralları — ALIŞ (`pages/invoices/InvoiceNew.js`) ve SATIŞ
 * (`pages/satis/SatisFaturaNew.js`) ekranlarının ORTAK kaynağı.
 *
 * Satış ekranı bir dönem alışın ESKİ bir kopyasıydı: ambalaj birimi/içeriği
 * yoktu, depo hem faturada hem kalemde soruluyordu (kullanıcı, 2026-10-01).
 * Kopyalar zamanla ayrışıyor; para ve stok doğuran kurallar burada TEK yerde.
 */

/**
 * Satır tutarı. Brüt ve iskonto AYRI AYRI iki haneye yuvarlanır; başlıktaki
 * "İskontosuz Toplam − İskonto = KDV Matrahı" ancak böyle birebir tutar
 * (`miktar*fiyat*(1-i/100)` tek seferde yuvarlanınca kuruş kayıyordu).
 */
export const satirTutari = (it) => {
  const miktar = parseFloat(it.quantity) || 0;
  const fiyat = parseFloat(it.unit_price) || 0;
  const oran = parseFloat(it.discount_rate) || 0;
  const brut = Number((miktar * fiyat).toFixed(2));
  const iskonto = Number((brut * oran / 100).toFixed(2));
  return { brut, iskonto, net: Number((brut - iskonto).toFixed(2)) };
};

/**
 * Kuruşa yuvarlama, YARIM YUKARI (0,005 → 0,01). `toFixed` ve
 * `Math.round(x*100)` 723,635'i 723,63 yapıyor: sayı bellekte 723,63499…
 * Önce 15 anlamlı haneye çekilir (yazıldığı ondalığa döner), sonra üs
 * yazımıyla yuvarlanır. Sunucu kopyası `invoices.kurus`.
 */
export const yuvarla2 = (x) => {
  const n = Number(x) || 0;
  const a = Number(Math.abs(n).toPrecision(15));
  if (a < 1e-6) return 0;
  return Math.sign(n) * Number(`${Math.round(Number(`${a}e2`))}e-2`);
};

/**
 * FATURA TOPLAMLARI — matrah, KDV, genel toplam (kullanıcı, 2026-10-14).
 * KDV yuvarlanmadan matraha ekleniyordu: 0160755 toplamı 25.096,785 olarak
 * saklandı, liste 25.096,78, fatura ekranı 25.096,79 gösterdi (433 fatura).
 * Kural: satır tutarları toplanır; satır KDV'leri toplanıp BİR KEZ
 * yuvarlanır (sunucunun `_kdv_topla`sı ile aynı — satır başına yuvarlamak
 * kayıtlı 238 faturanın KDV'sini değiştirirdi); KDV hariçte genel toplam =
 * matrah + KDV, KDV dahilde genel toplam satırların toplamıdır ve matrah
 * ondan KDV düşülerek bulunur. Üç rakam da kuruşludur, toplamları tutar.
 * `oranFn` kalemin KDV oranını, `tutarFn` satır tutarını verir.
 */
export const faturaToplamlari = (items, oranFn, dahil,
  tutarFn = (it) => parseFloat(it.total_price) || 0) => {
  let satirlar = 0;
  let kdvHam = 0;
  (items || []).forEach((it) => {
    const t = Number(tutarFn(it)) || 0;
    const o = parseFloat(oranFn(it)) || 0;
    satirlar += t;
    kdvHam += dahil ? (o ? t - t / (1 + o / 100) : 0) : t * o / 100;
  });
  const satirToplami = yuvarla2(satirlar);
  const kdv = yuvarla2(kdvHam);
  return dahil
    ? { matrah: yuvarla2(satirToplami - kdv), kdv, genel: satirToplami }
    : { matrah: satirToplami, kdv, genel: yuvarla2(satirToplami + kdv) };
};

// Faturalarda sık geçen yazımlar birimler tablosundaki kısaltmaya çekilir
// ("GR" tabloda yok, "g" var). Backend'deki eşlerle aynı tutulmalı.
const BIRIM_ESLERI = {
  gr: 'g', gram: 'g', grm: 'g',
  l: 'lt', litre: 'lt', lit: 'lt',
  mililitre: 'ml', mlt: 'ml', cc: 'ml',
};

/**
 * `kaynak` biriminden `hedef` birimine çarpan — YALNIZCA aynı ölçü grubu ve
 * grup `quantity` DEĞİLSE (sunucu: `unit_utils.metrik_carpan`). adet/koli/
 * paket grubunda taban faktörlerin hepsi 1'dir, oradan çevrim yanlış olur.
 * Döner: çarpan, aynı birimse 1, çevrilemezse null.
 */
export const birimCarpani = (units, kaynak, hedef) => {
  const a = BIRIM_ESLERI[(kaynak || '').toLowerCase()] || (kaynak || '').toLowerCase();
  const b = BIRIM_ESLERI[(hedef || '').toLowerCase()] || (hedef || '').toLowerCase();
  if (!a || !b || a === b) return 1;
  const u1 = (units || []).find(u => (u.abbreviation || '').toLowerCase() === a);
  const u2 = (units || []).find(u => (u.abbreviation || '').toLowerCase() === b);
  if (!u1 || !u2) return null;
  if (u1.unit_group !== u2.unit_group || u1.unit_group === 'quantity') return null;
  const f2 = parseFloat(u2.to_base_factor);
  if (!(f2 > 0)) return null;
  return parseFloat(u1.to_base_factor) / f2;
};

/**
 * AMBALAJ İÇERİĞİ HER ZAMAN ÜRÜNÜN ANA BİRİMİNDE tutulur. Kalemleri buna
 * göre düzeltir; değişiklik yoksa AYNI diziyi döndürür (efekt döngüye girmesin).
 *
 * (a) Etiket (`package_unit`) ana birimden farklıysa ve METRİK çevrilebiliyorsa
 *     değer çevrilir, çevrilemiyorsa yalnızca etiket ana birime düzelir.
 * (b) Ambalaj birimi ana birimle AYNIYSA içerik 1'dir — ama satır başına
 *     YALNIZCA BİR KEZ doldurulur (`_icerikDolduruldu`). İşaret olmadan
 *     kullanıcı alanı temizler temizlemez "1"e dönüyordu: yeni değer yazmak
 *     imkânsızlaşıyor, fiyat uyarısı da sönüyordu. İşaret kaydedilmez.
 *     Birim farklıysa doldurulmaz: orada içerik çevrimden gelir ve yazmak
 *     stoğu değiştirir — kırmızı "zorunlu" uyarısı o iş için.
 */
export const ambalajiNormallestir = (items, products, units) => {
  let degisti = false;
  const yeni = items.map(it => {
    const kart = products.find(p => p.id === it.matched_product_id);
    const ana = kart?.unit;
    if (!ana) return it;
    let sonuc = it;
    const mevcut = it.package_unit || '';
    if (mevcut.toLowerCase() !== ana.toLowerCase()) {
      const carpan = birimCarpani(units, mevcut, ana);
      const icerik = parseFloat(it.package_qty);
      degisti = true;
      sonuc = (carpan && carpan !== 1 && icerik > 0)
        ? { ...sonuc, package_qty: String(icerik * carpan), package_unit: ana }
        : { ...sonuc, package_unit: ana };
    }
    const ambalajBirimi = (sonuc.unit || '').toLowerCase();
    if (!(parseFloat(sonuc.package_qty) > 0)
        && !sonuc._icerikDolduruldu
        && ambalajBirimi && ambalajBirimi === ana.toLowerCase()) {
      degisti = true;
      sonuc = { ...sonuc, package_qty: '1', _icerikDolduruldu: true };
    }
    return sonuc;
  });
  return degisti ? yeni : items;
};

/**
 * İLK KALEMİN DEPOSU DİĞER SATIRLARI DOLDURUR (kullanıcı, 2026-09-22).
 * Ezilenler yalnızca BOŞ olanlar ya da ÖNCEKİ ilk-kalem değerini taşıyanlar:
 * kullanıcının bilerek farklı depo seçtiği satır korunur, hepsini koşulsuz
 * ezmek gerçek bir seçimi sessizce silerdi. `uygunMu` hangi satırların depo
 * taşıdığını söyler (alışta yalnızca stoklu tür).
 */
export const ilkDepoyuYay = (items, yeniDepo, uygunMu = () => true) => {
  const oncekiIlk = items[0]?.warehouse_id || '';
  return items.map((k, j) => {
    if (j === 0 || !uygunMu(k)) return k;
    const w = k.warehouse_id || '';
    return (!w || w === oncekiIlk) ? { ...k, warehouse_id: yeniDepo } : k;
  });
};
